'use client';

import React from 'react';
import { X, Star, TrendingUp, TrendingDown, Calendar, Clock, Tag, Image as ImageIcon, FileText, Lightbulb } from 'lucide-react';
import { StoredTrade } from '../utils/tradeStorage';

interface TradeDetailModalProps {
  trade: StoredTrade | null;
  onClose: () => void;
  onEdit: (trade: StoredTrade) => void;
}

const statusColors: Record<string, string> = {
  'Reviewed': 'bg-positive-subtle text-positive border border-positive/20',
  'Pending Review': 'bg-warning-subtle text-warning border border-warning/20',
  'Flagged': 'bg-negative-subtle text-negative border border-negative/20',
};

const strategyColors: Record<string, string> = {
  'Breakout': 'bg-neutral-subtle text-neutral-400',
  'Momentum': 'bg-primary/10 text-primary',
  'VWAP Reclaim': 'bg-warning-subtle text-warning',
  'Gap Fill': 'bg-purple-900/30 text-purple-400',
  'Mean Reversion': 'bg-negative-subtle text-negative',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={`detail-star-${star}`}
          size={14}
          className={star <= rating ? 'text-warning fill-warning' : 'text-muted'}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{rating}/5</span>
    </div>
  );
}

export default function TradeDetailModal({ trade, onClose, onEdit }: TradeDetailModalProps) {
  if (!trade) return null;

  const isProfit = trade.pnl >= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isProfit ? 'bg-positive/10' : 'bg-negative/10'}`}>
              {isProfit ? <TrendingUp size={16} className="text-positive" /> : <TrendingDown size={16} className="text-negative" />}
            </div>
            <div>
              <h2 className="text-base font-600 text-foreground">{trade.symbol} — {trade.direction}</h2>
              <p className="text-xs text-muted-foreground">{trade.dateISO} · {trade.strategy}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onClose(); onEdit(trade); }}
              className="px-3 py-1.5 rounded-lg text-xs font-500 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
            >
              Edit Trade
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* P&L Banner */}
          <div className={`rounded-xl border px-5 py-4 ${isProfit ? 'bg-positive-subtle border-positive/20' : 'bg-negative-subtle border-negative/20'}`}>
            <p className="text-xs text-muted-foreground mb-1">Net P&L</p>
            <p className={`text-3xl font-bold font-mono-nums ${isProfit ? 'text-positive' : 'text-negative'}`}>
              {isProfit ? '+' : ''}${trade.pnl.toLocaleString()}
            </p>
            <p className={`text-sm font-500 font-mono-nums mt-0.5 ${isProfit ? 'text-positive' : 'text-negative'}`}>
              {isProfit ? '+' : ''}{trade.pnlPct.toFixed(2)}% · R:R {trade.rr >= 0 ? '1:' : ''}{trade.rr.toFixed(1)}
            </p>
          </div>

          {/* Trade details grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: <Calendar size={13} />, label: 'Date', value: trade.dateISO },
              { icon: <Clock size={13} />, label: 'Timeframe', value: trade.timeframe || '—' },
              { icon: null, label: 'Entry Price', value: `$${trade.entry.toFixed(2)}` },
              { icon: null, label: 'Exit Price', value: `$${trade.exit.toFixed(2)}` },
              { icon: null, label: 'Position Size', value: `${trade.size} shares` },
              { icon: null, label: 'Stop Loss', value: trade.stopLoss ? `$${trade.stopLoss.toFixed(2)}` : '—' },
              { icon: null, label: 'Take Profit', value: trade.takeProfit ? `$${trade.takeProfit.toFixed(2)}` : '—' },
              { icon: <Tag size={13} />, label: 'Strategy', value: trade.strategy },
            ].map((item) => (
              <div key={`detail-${item.label}`} className="bg-muted/40 rounded-lg px-3 py-2.5">
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                  {item.icon}
                  {item.label}
                </p>
                <p className={`text-sm font-500 font-mono-nums ${
                  item.label === 'Strategy'
                    ? (strategyColors[item.value] || 'text-foreground').replace(/bg-\S+/g, '').trim()
                    : 'text-foreground'
                }`}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {/* Status + Rating */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Status</p>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-500 ${statusColors[trade.status]}`}>
                {trade.status}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Trade Quality</p>
              <StarRating rating={trade.rating} />
            </div>
            {trade.hasScreenshot && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ImageIcon size={13} />
                Screenshot attached
              </div>
            )}
          </div>

          {/* Notes */}
          {trade.notes && (
            <div>
              <p className="text-xs font-500 text-foreground flex items-center gap-1.5 mb-2">
                <FileText size={13} className="text-muted-foreground" />
                Trade Notes
              </p>
              <p className="text-sm text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 leading-relaxed">
                {trade.notes}
              </p>
            </div>
          )}

          {/* Lessons */}
          {trade.lessons && (
            <div>
              <p className="text-xs font-500 text-foreground flex items-center gap-1.5 mb-2">
                <Lightbulb size={13} className="text-warning" />
                Lessons Learned
              </p>
              <p className="text-sm text-muted-foreground bg-warning-subtle border border-warning/10 rounded-lg px-3 py-2.5 leading-relaxed">
                {trade.lessons}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
